import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { FaHome, FaGraduationCap, FaQuestionCircle, FaChartLine, FaFire, FaTrophy, FaCoins, FaCheckCircle, FaSignOutAlt, FaUserShield, FaUser, FaComments, FaUsers, FaFlask, FaLifeRing, FaMoon, FaSun } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import AuthService from '../services/AuthService';
import FactsService from '../services/FactsService';
import ProgressService from '../services/ProgressService';
import LeagueService from '../services/LeagueService';
import soundEffects from '../utils/soundEffects';
import { createAchievementConfetti, createSuccessConfetti, createLevelUpConfetti } from '../utils/confetti';
import { getLevelTitle, getLevelProgress, getXpForLevel } from '../utils/levelUtils';
import Badge from './Badge';
import BadgeModal from './BadgeModal';
import BadgeService from '../utils/badgeService';
import BottomNav from './BottomNav';
import ProfileMenu from './ProfileMenu';
import AdBanner from './AdBanner';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { state, actions } = useUser();
  const [dailyFact, setDailyFact] = useState(null);
  const [factCompleted, setFactCompleted] = useState(false);
  const [canCompleteToday, setCanCompleteToday] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [myClass, setMyClass] = useState(null);
  const [loadingClass, setLoadingClass] = useState(false);
  const lastSyncRef = useRef(null);

  // Authentication check
  useEffect(() => {
    const checkAuth = async () => {
      try {
        
        const token = localStorage.getItem('moneysmart-token');
        
        // Check if user is authenticated
        const isAuth = AuthService.isAuthenticated();
        
        if (!isAuth) {
          navigate('/login', { replace: true });
          return;
        }

        // Check if user exists in context
        
        if (!state.user) {
          navigate('/login', { replace: true });
          return;
        }

        setIsAuthenticated(true);
      } catch (error) {
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, state.user]);

  // Load student's class if they are a student
  useEffect(() => {
    const loadMyClass = async () => {
      // Only try to load class if user has a leagueId (is a student)
      if (isAuthenticated && state.user?.leagueId) {
        try {
          setLoadingClass(true);
          const data = await LeagueService.getMyClass();
          setMyClass(data);
        } catch (error) {
          // Silently fail - user might not be in a class anymore
          // Only log if it's not a 403 (not a student) or 404 (not in class)
          if (error.status !== 403 && error.status !== 404) {
            console.log('Could not load class:', error);
          }
          setMyClass(null);
        } finally {
          setLoadingClass(false);
        }
      } else {
        // Not a student, don't try to load
        setMyClass(null);
      }
    };

    if (isAuthenticated && state.user) {
      loadMyClass();
    }
  }, [isAuthenticated, state.user]);

  // Auto-sync functionality
  useEffect(() => {
    if (!isAuthenticated) return;

    const autoSync = async (showNotification = false) => {
      // Prevent duplicate syncs within 1 second
      const now = Date.now();
      if (lastSyncRef.current && (now - lastSyncRef.current) < 1000) {
        return;
      }
      
      lastSyncRef.current = now;
      
      try {
        const success = await actions.syncWithBackend();
        if (success) {
          setLastSyncTime(new Date());
          if (showNotification) {
            toast.success('Data synced automatically!', { duration: 2000 });
          }
        } else {
          if (showNotification) {
            toast.error('Sync failed, using cached data', { duration: 2000 });
          }
        }
      } catch (error) {
        if (showNotification) {
          toast.error('Sync error occurred', { duration: 2000 });
        }
      }
    };

    // Initial sync when component mounts
    autoSync();

    // Set up periodic auto-sync every 5 minutes
    const syncInterval = setInterval(autoSync, 5 * 60 * 1000);

    // Set up visibility change sync (when user returns to tab)
    // This is the primary handler for tab switching
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        autoSync(true); // Show notification when returning to tab
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(syncInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, actions]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const loadTodaysFact = async () => {
      try {
        const response = await FactsService.getTodaysFact();
        
        if (response.fact) {
          const fact = response.fact;
          // Check if the fact is already completed and if user can complete today
          const isCompleted = fact.isCompleted || false;
          const canComplete = fact.canCompleteToday !== false;
          
          setDailyFact(fact);
          setFactCompleted(isCompleted);
          setCanCompleteToday(canComplete);
          actions.setDailyFact(fact);
          
        } else {
          toast.error('Failed to load today\'s fact');
        }
      } catch (error) {
        toast.error('Failed to load today\'s fact');
        
        // Fallback to a default fact if backend fails
        const fallbackFact = {
          id: 1,
          title: "Welcome to InvestNYou!",
          content: "Start your financial journey by learning about investing, budgeting, and building wealth. Complete daily facts to earn XP and level up!",
          category: "Getting Started",
          xpValue: 25,
          isCompleted: false
        };
        setDailyFact(fallbackFact);
        actions.setDailyFact(fallbackFact);
      }
    };

    loadTodaysFact();
  }, [isAuthenticated, actions]);

  const handleFactComplete = async () => {
    if (!dailyFact || factCompleted || !canCompleteToday) return;

    try {
      const response = await ProgressService.completeFact(dailyFact.id);
      
      if (response.xpEarned) {
        setFactCompleted(true);
        setCanCompleteToday(false);
        
        // Gamification effects
        soundEffects.playSuccess();
        const factCard = document.querySelector('.daily-fact-card');
        if (factCard) {
          createSuccessConfetti(factCard);
        }
        
        toast.success(`+${response.xpEarned} XP earned!`);
        
        // Auto-sync with backend to get the latest data
        const syncSuccess = await actions.syncWithBackend();
        
        if (syncSuccess) {
          setLastSyncTime(new Date());
        } else {
          // Fallback: update local state if sync fails
          actions.completeFact(dailyFact.id);
          actions.addXp(response.xpEarned);
        }
        
        // Check if user leveled up
        if (response.newLevel > state.progress?.level) {
          soundEffects.playLevelUp();
          const dashboardContainer = document.querySelector('.dashboard-container') || document.body;
          createLevelUpConfetti(dashboardContainer);
          toast.success(`🎉 Level Up! You're now level ${response.newLevel}!`);
        }
        
        // Show streak update with animation
        if (response.newStreak && response.newStreak > (state.progress.streak || 0)) {
          // Trigger streak animation
          const streakElement = document.querySelector('.streak-display-number');
          if (streakElement) {
            streakElement.style.animation = 'streakPulse 0.6s ease-out';
            setTimeout(() => {
              streakElement.style.animation = '';
            }, 600);
          }
          toast.success(`🔥 ${response.newStreak} day streak!`);
        }
        
        // Show badge unlock if any
        if (response.badgeUnlocked) {
          toast.success(`🏆 Badge unlocked: ${response.badgeUnlocked.name}!`, {
            duration: 4000,
            icon: '🏆'
          });
        }
        
        // Check for additional badges that might have been earned
        if (syncSuccess) {
          const updatedProgress = await ProgressService.getProgress();
          const newlyAwarded = await BadgeService.checkAndAwardBadges(
            updatedProgress.progress,
            { factCompleted: true }
          );
          
          if (newlyAwarded.length > 0) {
            soundEffects.playAchievement();
            newlyAwarded.forEach((badge, index) => {
              setTimeout(() => {
                const factCard = document.querySelector('.daily-fact-card');
                if (factCard) {
                  createAchievementConfetti(factCard);
                }
                toast.success(`🏆 Badge unlocked: ${badge.name}!`, {
                  duration: 4000,
                  icon: '🏆'
                });
              }, index * 300);
            });
            // Sync again to get updated badges
            await actions.syncWithBackend();
          }
        }
        
      }
    } catch (error) {
      console.error('Complete fact error:', error);
      const errorMessage = error.message || 'Failed to complete fact';
      
      // Check for daily limit error (case-insensitive)
      if (errorMessage.toLowerCase().includes('one fact per day') || 
          errorMessage.toLowerCase().includes('already completed')) {
        toast.error('You can only complete one fact per day! Come back tomorrow for a new fact.');
        setCanCompleteToday(false);
        setFactCompleted(true); // Mark as completed to prevent further attempts
      } else if (errorMessage.toLowerCase().includes('already completed')) {
        toast.error('You have already completed this fact!');
        setFactCompleted(true);
      } else {
        toast.error(errorMessage || 'Failed to complete fact. Please try again.');
      }
    }
  };



  const handleLogout = () => {
    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to log out?');
    
    if (!confirmed) {
      return;
    }
    
    // User confirmed logout, clearing all data
    
    // Clear all authentication data
    AuthService.logout();
    
    // Clear user context completely
    actions.setUser(null);
    actions.setOnboarded(false);
    
    // Clear any cached data
    localStorage.clear();
    sessionStorage.clear();
    
    // Show success message
    toast.success('Logged out successfully');
    
    // Force redirect to landing page
    navigate('/', { replace: true });
    
    // Force page reload to ensure clean state
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="dashboard-container content-with-nav">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-bar-content">
          <div className="top-bar-left">
            <div className="logo-container">
              <img src="/LogoGurt.jpg" alt="InvestNYou Logo" className="logo-img" />
            </div>
            <h1 className="app-title">InvestNYou</h1>
          </div>
          
          <ProfileMenu showLevel />
        </div>
      </div>

      <div className="dashboard-content">
        {/* Ad slot — only renders for free-tier users */}
        <AdBanner placement="dashboard" />

        {/* Greeting */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="greeting-section"
        >
          <h2 className="greeting-title">
            Hey {state.user?.username || state.user?.name || 'Guest'}! 👋
          </h2>
        </motion.div>

        {/* Streak Display - Big and Centered */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.05, type: 'spring', stiffness: 200 }}
          className="streak-display-card"
        >
          <div className="streak-display-content">
            <FaFire className="streak-display-icon" />
            <div className="streak-display-text">
              <div className="streak-display-number">{state.progress.streak || 0}</div>
              <div className="streak-display-label">Day Streak</div>
            </div>
          </div>
        </motion.div>

        {/* Progress Snapshot */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="progress-card"
        >
          <div className="progress-header">
            <div className="level-display">
              <div className="level-label">Level</div>
              <div className="level-number">{state.progress.level}</div>
            </div>
            <div className="xp-display">
              <FaCoins className="xp-icon" />
              <span className="xp-text">
                {state.progress.xp || 0}/{getXpForLevel(state.progress.level + 1)} XP
              </span>
            </div>
            {lastSyncTime && (
              <div className="sync-info" title={`Last synced: ${lastSyncTime.toLocaleTimeString()}`}>
                <div className="sync-indicator">
                  <div className="sync-dot"></div>
                  <span>Auto-synced</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill"
              style={{ 
                width: `${getLevelProgress(state.progress.xp, state.progress.level).progressPercentage}%`
              }}
            />
          </div>
          <div className="progress-text">
            {getLevelProgress(state.progress.xp, state.progress.level).xpNeededForNext} XP to next level
          </div>
        </motion.div>

        {/* Daily Fact Card */}
        {dailyFact && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className={`fact-card ${factCompleted ? 'completed' : ''}`}
          >
            <div className="fact-card-header">
              <h3 className="fact-card-title">Here's your daily fact!</h3>
            </div>
            <div className="fact-card-content">
              <div className="fact-category">
                {dailyFact.category}
              </div>
              <h3 className="fact-title">
                {dailyFact.title}
              </h3>
              <p className="fact-content">
                {dailyFact.content}
              </p>
            </div>

            {!factCompleted && canCompleteToday && (
              <div className="mt-6">
                <button
                  onClick={handleFactComplete}
                  className="fact-button"
                >
                  Got it! +{dailyFact.xpValue || dailyFact.xp} XP
                </button>
              </div>
            )}

            {!canCompleteToday && !factCompleted && (
              <div className="fact-completed-message">
                <div className="fact-completed-content">
                  <p className="fact-completed-text">
                    ⏰ You've already completed your daily fact today!
                  </p>
                  <p className="fact-completed-subtext">
                    Come back tomorrow for a new fact and to continue your streak!
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Daily Fact Completed Message */}
        {factCompleted && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="completed-fact-card"
          >
            <div className="completed-fact-content">
              <div className="completed-icon-container">
                <FaCheckCircle className="completed-icon" />
              </div>
              <h3 className="completed-title">
                Daily Fact Completed! 🎉
              </h3>
              <p className="completed-text">
                Great job! Come back tomorrow for your next daily fact.
              </p>
              {state.progress.streak > 1 && (
                <div className="streak-card">
                  <p className="streak-text-completed">
                    🔥 {state.progress.streak} day streak!
                  </p>
                  <p className="streak-subtext">
                    Keep it up to unlock special badges!
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* My Class Section - Only for students */}
        {myClass && !loadingClass && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="class-card"
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              marginBottom: '0.75rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <FaUsers style={{ fontSize: '1.5rem', color: '#3b82f6' }} />
              <div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', margin: 0 }}>
                  My Class
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                  {myClass.league.name}
                </p>
                {myClass.league.code && (
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0 0' }}>
                    Code: {myClass.league.code}
                  </p>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  soundEffects.playClick();
                  navigate('/class');
                }}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '0.75rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  soundEffects.playHover();
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#3b82f6';
                }}
              >
                <FaGraduationCap /> View Assignments
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  soundEffects.playClick();
                  navigate('/challenges');
                }}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '0.75rem',
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#d97706';
                  soundEffects.playHover();
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f59e0b';
                }}
              >
                <FaTrophy /> League Challenges
              </button>
            </div>

            {myClass.students && myClass.students.length > 0 ? (
              <div>
                <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
                  Classmates ({myClass.students.length})
                </p>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '0.75rem',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {myClass.students.map((student) => (
                    <div
                      key={student.id}
                      style={{
                        padding: '0.75rem',
                        background: student.id === state.user?.id ? '#eff6ff' : '#f9fafb',
                        borderRadius: '8px',
                        border: student.id === state.user?.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                        {student.username}
                        {student.id === state.user?.id && (
                          <span style={{ marginLeft: '0.25rem', fontSize: '0.75rem', color: '#3b82f6' }}>(You)</span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        Level {student.level || 1}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                        {student.xp || 0} XP
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p style={{ fontSize: '0.875rem', color: '#6b7280', textAlign: 'center', padding: '1rem' }}>
                No classmates found
              </p>
            )}
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="quick-actions"
        >
          <button
            onClick={(e) => {
              e.preventDefault();
              soundEffects.playClick();
              setTimeout(() => navigate('/learn'), 200);
            }}
            className="action-button action-button-primary"
          >
            <FaGraduationCap className="action-icon learn" />
            <h3 className="action-title">Start Learning</h3>
            <p className="action-subtitle">Explore courses</p>
          </button>

          <button
            onClick={(e) => {
              e.preventDefault();
              soundEffects.playClick();
              setTimeout(() => navigate('/ask'), 200);
            }}
            className="action-button"
          >
            <FaQuestionCircle className="action-icon ask" />
            <h3 className="action-title">Ask Question</h3>
            <p className="action-subtitle">Get instant help</p>
          </button>

          <button
            onClick={(e) => {
              e.preventDefault();
              soundEffects.playClick();
              setTimeout(() => navigate('/leaderboard'), 200);
            }}
            className="action-button action-button-primary"
          >
            <FaTrophy className="action-icon" />
            <h3 className="action-title">Leaderboard</h3>
            <p className="action-subtitle">View rankings</p>
          </button>

          <button
            onClick={(e) => {
              e.preventDefault();
              soundEffects.playClick();
              setTimeout(() => navigate('/challenges'), 200);
            }}
            className="action-button action-button-primary"
          >
            <FaTrophy className="action-icon" />
            <h3 className="action-title">Challenges</h3>
            <p className="action-subtitle">Join competitions</p>
          </button>

          {(state.user?.isAdmin || state.user?.adminLevel === '1' || state.user?.adminLevel === '2' || state.user?.adminLevel === '3' || state.user?.adminLevel === 'master_admin' || state.user?.isTeacher) && (
            <button
              onClick={(e) => {
                e.preventDefault();
                soundEffects.playClick();
                setTimeout(() => navigate('/leagues'), 200);
              }}
              className="action-button action-button-primary"
            >
              <div className="action-icon-wrapper leagues-icon-wrapper">
                <FaUsers className="action-icon leagues" />
              </div>
              <h3 className="action-title">League Management</h3>
              <p className="action-subtitle">Manage classes</p>
            </button>
          )}

        </motion.div>

        {/* Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="badges-card"
        >
          <div className="badges-header">
            <h3 className="badges-title">Your Badges</h3>
            <button
              onClick={() => setShowBadgeModal(true)}
              className="view-all-badges-btn"
            >
              View All
            </button>
          </div>
          {state.progress.badges && state.progress.badges.length > 0 ? (
            <div className="badges-list">
              {state.progress.badges.slice(0, 6).map((badge) => (
                <Badge
                  key={badge.id || badge}
                  badge={badge}
                  size="medium"
                  onClick={() => setShowBadgeModal(true)}
                />
              ))}
              {state.progress.badges.length > 6 && (
                <button
                  onClick={() => setShowBadgeModal(true)}
                  className="badge-more-btn"
                >
                  +{state.progress.badges.length - 6} more
                </button>
              )}
            </div>
          ) : (
            <div className="badges-empty">
              <FaTrophy className="badges-empty-icon" />
              <p className="badges-empty-text">No badges yet. Start learning to earn your first badge!</p>
            </div>
          )}
        </motion.div>
        
        {/* Badge Modal */}
        <BadgeModal
          isOpen={showBadgeModal}
          onClose={() => setShowBadgeModal(false)}
          userBadges={state.progress.badges || []}
          userProgress={state.progress}
        />

        {/* Footer Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="dashboard-footer"
        >
          <div className="dashboard-footer-links">
            <Link to="/help" className="dashboard-footer-link">
              Help
            </Link>
            <Link to="/terms" className="dashboard-footer-link">
              Terms of Service
            </Link>
            <Link to="/privacy" className="dashboard-footer-link">
              Privacy Policy
            </Link>
            <Link to="/patchnotes" className="dashboard-footer-link">
              Patch Notes
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default Dashboard;
